import React, { useState, useEffect, Dispatch, SetStateAction } from "react";
import {
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Avatar,
  InputAdornment,
  Alert,
} from "@mui/material";
import { AccountCircle, Email, Lock } from "@mui/icons-material";

import { User } from '../types';

interface ProfileProps {
  setUser: Dispatch<SetStateAction<User | null>>;
}

const Profile: React.FC<ProfileProps> = ({ setUser }) => {
  const [userData, setUserData] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // --- Läser localStorage när komponenten mountas ---
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("token");

    if (storedUser && storedToken) {
      const parsedUser = JSON.parse(storedUser);
      setUserData(parsedUser);
      setToken(storedToken);
  
    }
  }, []);

  const isLoggedIn = !!token && !!userData?.userId && !!userData?.username;

  const handleSave = async () => {
    try {
      setMessage("");
      setError("");

      if (!token) {
        setError("Du är inte inloggad");
        return;
      }

      if (password && password !== confirmPassword) {
        setError("Nytt lösenord och bekräfta lösenord matchar inte");
        return;
      }

      const baseUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5001";

      const res = await fetch(`${baseUrl}/api/auth/update-profile`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          username: username || undefined,
          email: email || undefined,
          oldPassword: password ? oldPassword : undefined,
          password: password || undefined,
        }),
      });

      let data;
      try {
        data = await res.json();
      } catch (err) {
        const text = await res.text();
        console.error("Backend svarade inte med JSON:", text);
        setError("Server svarade inte korrekt");
        return;
      }

      if (!res.ok) {
        setError(data.message || "Något gick fel");
        return;
      }

      const updatedUser: User = {
        userId: data.user.userId,
        username: data.user.username,
        email: data.user.email,
        avatar: data.user.avatar || null,
        token: token,
      };

      // Uppdatera state och både localStorage och sessionStorage
      setUserData(updatedUser);
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
      sessionStorage.setItem("user", JSON.stringify(updatedUser));

      setMessage("Profil uppdaterad!");
      setPassword("");
      setConfirmPassword("");
      setOldPassword("");
    } catch (err) {
      console.error(err);
      setError("Serverfel");
    }
  };

  if (!isLoggedIn) {
    return (
      <Box
        sx={{
          minHeight: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 2,
        }}
      >
        <Typography variant="h5">Du är inte inloggad.</Typography>
      </Box>
    );
  }

  return (
<Box
  sx={{
    minHeight: "80vh",
    display: "flex",
    justifyContent: "center",
    padding: 2,
    paddingBottom: "100px", // extra utrymme för footer
    background: "linear-gradient(to bottom, #f0f4f8, #d9e2ec)",
    overflowY: "auto",
  }}
>

  <Paper
    sx={{
      mt: { xs: 2, sm: 0 }, // lägg lite marginal på toppen för små skärmar
      mb: { xs: 2, sm: 0 }, // lite bottenmarginal
      padding: { xs: 2, sm: 4 },
      maxWidth: { xs: "95%", sm: 500 },
      width: "100%",
      borderRadius: 3,
      backgroundColor: "#ffffffcc",
      backdropFilter: "blur(10px)",
    }}
    elevation={6}
  >
        {/* Avatar */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <Avatar src={userData?.avatar || undefined} sx={{ width: 80, height: 80 }} />
        </Box>

        <Typography variant="h4" align="center" gutterBottom>
          Min Profil
        </Typography>

        {/* --- Read-only kontaktuppgifter --- */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: "#f5f5f5", borderRadius: 2 }}>
          <Typography variant="subtitle1">
            <strong>Användarnamn:</strong> {userData?.username}
          </Typography>
          <Typography variant="subtitle1">
            <strong>E-post:</strong> {userData?.email}
          </Typography>
        </Box>

        {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* --- Redigeringsfält --- */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nytt användarnamn"
              fullWidth
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><AccountCircle /></InputAdornment> }}
              placeholder="Fyll i nytt användarnamn"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Ny E-post"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Email /></InputAdornment> }}
              placeholder="Fyll i ny e-post"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
              Byt lösenord
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Gammalt lösenord"
              type="password"
              fullWidth
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Lock /></InputAdornment> }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Nytt lösenord"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Lock /></InputAdornment> }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Bekräfta nytt lösenord"
              type="password"
              fullWidth
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Lock /></InputAdornment> }}
            />
          </Grid>

          <Grid item xs={12} textAlign="center">
            <Button
              onClick={handleSave}
              variant="contained"
              sx={{
                width: { xs: "100%", sm: "auto" },
                py: 1.5,
                borderRadius: 3,
                fontWeight: "bold",
                background: "linear-gradient(to right, #4dabf5, #1976d2)",
              }}
            >
              Spara ändringar
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Profile;
